import * as React from 'react';
import {
    Box,
    Button,
    Card,
    CardContent,
    Stack,
    TextField,
    Typography,
    Link
} from '@mui/material';
import Api from '../services/Api';

export interface ILoginProps {
    // Opcional: callback para redirigir tras login exitoso
    onLoginSuccess?: () => void;
}

export interface ILoginState {
    email: string;
    password: string;
    loading: boolean;
    error: string | null;
}

export default class Login extends React.Component<ILoginProps, ILoginState> {
    public constructor(props: ILoginProps) {
        super(props);

        this.state = {
            email: '',
            password: '',
            loading: false,
            error: null
        };
    }

    private handleInputChange =
        (field: 'email' | 'password') =>
            (event: React.ChangeEvent<HTMLInputElement>) => {
                this.setState({
                    [field]: event.target.value
                } as Pick<ILoginState, 'email' | 'password'>);
            };

    private handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        const { email, password } = this.state;

        if (!email || !password) {
            this.setState({ error: 'Por favor, introduce usuario y contraseña.' });
            return;
        }

        this.setState({ loading: true, error: null });

        try {
            // Llamada real a la API de login
            const response = await Api.post("/api/Auth/login", { email, password });

            if (!response.ok) {
                throw new Error('Credenciales incorrectas.');
            }

            const data = await response.json();

            // Guardar token y datos de usuario (ajusta nombres según tu backend)
            if (data.token) {
                localStorage.setItem('vg_token', data.token);
            }
            if (data.user) {
                localStorage.setItem('vg_user', JSON.stringify(data.user));
            }

            // Redirección tras login
            if (this.props.onLoginSuccess) {
                this.props.onLoginSuccess();
            } else {
                // Fallback simple: cambia la URL (ajusta la ruta a lo que definas)
                window.location.href = '/';
            }
        } catch (error: any) {
            this.setState({
                error: error.message || 'Error al iniciar sesión.',
                loading: false
            });
            return;
        }

        this.setState({ loading: false });
    };

    public render(): React.ReactElement<ILoginProps> {
        const { email, password, loading, error } = this.state;

        return (
            <Box
                sx={{
                    position: 'relative',
                    width: '100vw',
                    height: '100vh',
                    overflow: 'hidden'
                }}
            >
                {/* 🎥 Vídeo de fondo a pantalla completa */}
                <Box
                    component="video"
                    autoPlay
                    muted
                    loop
                    sx={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        minWidth: '100%',
                        minHeight: '100%',
                        objectFit: 'cover',
                        zIndex: -1
                    }}
                >
                    <source src="/videos/vivogolf-background.mp4" type="video/mp4" />
                    {/* Texto fallback si el vídeo no se puede reproducir */}
                    Tu navegador no soporta vídeo HTML5.
                </Box>

                {/* Capa de oscurecimiento ligera para mejorar legibilidad */}
                <Box
                    sx={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        bgcolor: 'rgba(0,0,0,0.45)',
                        zIndex: 0
                    }}
                />

                {/* Contenedor centrado del formulario */}
                <Box
                    sx={{
                        position: 'relative',
                        zIndex: 1,
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        px: 2
                    }}
                >
                    <Card
                        sx={{
                            maxWidth: 420,
                            width: '100%',
                            boxShadow: 8,
                            bgcolor: 'rgba(255,255,255,0.95)',
                            backdropFilter: 'blur(4px)'
                        }}
                    >
                        <CardContent>
                            <Stack spacing={3}>
                                {/* Logo + título */}
                                <Stack spacing={1} alignItems="center">
                                    {/* Sustituye por tu logotipo real */}
                                    <Box
                                        component="img"
                                        src="/images/vivogolf-logo.png"
                                        alt="VivoGolf"
                                        sx={{ height: 64, mb: 1 }}
                                    />
                                    <Typography variant="h5" fontWeight={700}>
                                        VivoGolf Admin
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Inicia sesión para gestionar torneos, minijuegos y jugadores.
                                    </Typography>
                                </Stack>

                                {/* Formulario */}
                                <Box component="form" onSubmit={this.handleSubmit} noValidate>
                                    <Stack spacing={2.5}>
                                        <TextField
                                            label="Usuario / Email"
                                            variant="outlined"
                                            fullWidth
                                            value={email}
                                            onChange={this.handleInputChange('email')}
                                            autoComplete="username"
                                            size='small'
                                        />
                                        <TextField
                                            label="Contraseña"
                                            type="password"
                                            variant="outlined"
                                            fullWidth
                                            value={password}
                                            onChange={this.handleInputChange('password')}
                                            autoComplete="current-password"
                                            size='small'
                                        />

                                        {error && (
                                            <Typography variant="body2" color="error">
                                                {error}
                                            </Typography>
                                        )}

                                        <Button
                                            type="submit"
                                            variant="contained"
                                            fullWidth
                                            disabled={loading}
                                            sx={{ py: 1.2, fontWeight: 600 }}
                                        >
                                            {loading ? 'Iniciando sesión...' : 'Entrar'}
                                        </Button>

                                        <Stack direction="row" justifyContent="flex-end">
                                            <Link
                                                href="/forgot-password"
                                                underline="hover"
                                                color="primary"
                                                variant="body2"
                                            >
                                                He olvidado mi contraseña
                                            </Link>
                                        </Stack>
                                    </Stack>
                                </Box>
                            </Stack>
                        </CardContent>
                    </Card>
                </Box>
            </Box>
        );
    }
}
